import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import SecurSpaceContainer from "../../components/common/SecurSpaceContainer";
import { BreadCrumb, PageHeader } from "../../components/common/PageHeader";
import InfoBanner from "../../components/location/profile/InfoBanner/InfoBanner";
import { Box, Button, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  AccessTimeFilledRounded,
  BlockRounded,
  CheckCircleRounded,
  EditRounded,
} from "@mui/icons-material";
import Busy from "../../components/Busy";
import { AppContext } from "../../context/app-context";
import LocationListTabs from "../../components/location/profile/LocationListTabs/LocationListTabs";
import { LocationTabConfig } from "../../components/location/profile/LocationListTabs/LocationTabConfig";
import EmailVerifyGuardTooltip from "../../components/notifications/EmailVerifyGuardTooltip";
import {searchSupplierLocations} from "../../components/location/requests/location-requests";
import GlobalCapacityBulkUpload from "../../components/location/capacity/GlobalCapacityBulkUpload";
import {useSnackbar} from "../../context/snackbar-context";
import {getErrorMessage} from "../../util/NetworkErrorUtil";
import {
  SupplierLocationDisplayVO,
} from "../../types/LocationStatus";
import { toSupplierLocationDisplayVO } from "./toSupplierLocationDisplayVO";
import { SupplierLocationListingVO } from "../../types/model/valueObject/location/SupplierLocationListingVO";
import {Account} from "../../types/Account";
import {useNavigate} from "react-router";
import {canManageCapacitySchedule, hasOwnerAccess} from "../../util/AccountUtils";
import { useFeatureFlags } from "../../hooks/routing/useFeatureFlags";
import { useAssistantApplied } from "../../context/assistant-context";

// Phones: the label sits above full-width buttons.
const SectionHeading = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: "14px",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
  },
}));

const MyLocationsLabel = styled(Box)({
  fontSize: "0.7857rem",
  fontWeight: 700,
  letterSpacing: "0.8px",
  textTransform: "uppercase",
  color: "#66727e",
});

/** Static tab config. Icons mirror the workflow step variants (filled glyphs for tabs). */
const LOCATION_TAB_CONFIGS: LocationTabConfig[] = [
  { variant: "active",      icon: <CheckCircleRounded fontSize="small" />,       label: "Active" },
  { variant: "pending",     icon: <AccessTimeFilledRounded fontSize="small" />,  label: "Pending" },
  { variant: "draft",       icon: <EditRounded fontSize="small" />,        label: "Draft" },
  { variant: "deactivated", icon: <BlockRounded fontSize="small" />,       label: "Deactivated" },
];

interface LocationsProfileProps {
  account?: Pick<Account, 'emailVerified'>;
}

const LocationsProfile = ({ account }: LocationsProfileProps) => {
  const { user } = useContext(AppContext);
  const {snackbarShowMessage} = useSnackbar();
  const navigate = useNavigate();
  const { supplierCapacitySchedule } = useFeatureFlags();

  const [locations, setLocations] = useState<SupplierLocationDisplayVO[]>([]);
  const [infoBannerExpanded, setInfoBannerExpanded] = useState(false);
  // POC: whether the global (all-locations) capacity bulk upload dialog is open.
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  const fetchLocations = useCallback(async (): Promise<SupplierLocationListingVO[]> => {
    Busy.set(true);
    try {
      const response = await searchSupplierLocations();
      const mapped: SupplierLocationDisplayVO[] = response.body
        ? response.body.map(toSupplierLocationDisplayVO)
        : [];
      setLocations(mapped);
      return mapped;
    } catch (err: any) {
      setLocations([]);
      snackbarShowMessage(getErrorMessage(err), 'error');
      return [];
    } finally {
      Busy.set(false);
    }
  }, [snackbarShowMessage]);

  const hasInitializedBanner = useRef(false);

  useEffect(() => {
    const initialize = async () => {
      const result = await fetchLocations();
      if (!hasInitializedBanner.current) {
        hasInitializedBanner.current = true;
        if (result) setInfoBannerExpanded(result.length === 0);
      }
    };
    void initialize();
  }, [fetchLocations]);

  // The assistant is mounted app-wide, so it cannot call back into this page
  // directly; this keeps the list fresh when it saves a change while the user
  // is looking at it.
  useAssistantApplied(useCallback(() => { void fetchLocations(); }, [fetchLocations]));


  const handleEdit = useCallback((location: SupplierLocationDisplayVO) => {
    navigate(`/locations/${location.locationId}/edit`);
  }, [navigate]);

  const handleCreate = useCallback(() => {
    navigate('/locations/create');
  }, [navigate]);

  const handleDeactivateSuccess = useCallback((location: SupplierLocationDisplayVO) => {
    snackbarShowMessage(`Successfully deactivated ${location.locationName}!`, 'success');
    void fetchLocations();
  }, [fetchLocations, snackbarShowMessage]);

  const handleDeleteSuccess = useCallback((location: SupplierLocationDisplayVO) => {
    snackbarShowMessage(`Successfully deleted ${location.locationName}!`, 'success');
    void fetchLocations();
  }, [fetchLocations, snackbarShowMessage]);

  const handleSubmitForReviewSuccess = useCallback((location: SupplierLocationDisplayVO) => {
    snackbarShowMessage(`${location.locationName} has been submitted for review!`, 'success');
    void fetchLocations();
  }, [fetchLocations, snackbarShowMessage]);

  const handleSubmitForReviewFailure = useCallback((msg: string) => {
    snackbarShowMessage(`Failed to submit for review. ${msg}`, 'error');
  }, [snackbarShowMessage]);

  const handleCloneSuccess = useCallback((location: SupplierLocationDisplayVO) => {
    snackbarShowMessage(
      `Successfully cloned "${location.locationName}"! The new location has been saved as a Draft.`,
      'success'
    );
    void fetchLocations();
  }, [fetchLocations, snackbarShowMessage]);

  const handleCloneFailure = useCallback((msg: string) => {
    snackbarShowMessage(`Failed to clone location. ${msg}`, 'error');
  }, [snackbarShowMessage]);


  const isEmailVerified = account?.emailVerified ?? false;
  // Cloning is restricted to Account Owners and Account Admins (excludes gate managers).
  const canClone = hasOwnerAccess(user);

  // This should not be possible via normal navigation
  if (!user) {
    return <Busy />;
  }

  return (
    <SecurSpaceContainer>
      <PageHeader title="Manage Locations">
        <BreadCrumb>Account</BreadCrumb>
        <BreadCrumb>Manage Locations</BreadCrumb>
      </PageHeader>
      <InfoBanner isExpanded={infoBannerExpanded} onExpandToggle={() => setInfoBannerExpanded((prev) => !prev)} />
      <SectionHeading>
        <MyLocationsLabel>My Locations</MyLocationsLabel>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}>
          {supplierCapacitySchedule && canManageCapacitySchedule(user) && locations.length > 0 && (
            <EmailVerifyGuardTooltip disabled={!isEmailVerified}>
              <Button variant="outlined" color="primary" onClick={() => setBulkUploadOpen(true)}>
                Bulk Upload Capacity
              </Button>
            </EmailVerifyGuardTooltip>
          )}
          <EmailVerifyGuardTooltip disabled={!isEmailVerified}>
            <Button variant="contained" color="primary" onClick={handleCreate}>
              {locations.length > 0 ? "Create Another Location" : "Create Location"}
            </Button>
          </EmailVerifyGuardTooltip>
        </Stack>
      </SectionHeading>
      <LocationListTabs
        tabs={LOCATION_TAB_CONFIGS}
        locations={locations}
        onEdit={handleEdit}
        onDeactivateSuccess={handleDeactivateSuccess}
        onDeactivateFailure={(msg) =>  snackbarShowMessage(`Failed to deactivate. ${msg}`, 'error')}
        onDeleteSuccess={handleDeleteSuccess}
        onDeleteFailure={(msg) => snackbarShowMessage(`Failed to delete. ${msg}`, 'error')}
        onSubmitForReviewSuccess={handleSubmitForReviewSuccess}
        onSubmitForReviewFailure={handleSubmitForReviewFailure}
        onCloneSuccess={handleCloneSuccess}
        onCloneFailure={handleCloneFailure}
        canClone={canClone}
        onCapacityUpdated={() => { void fetchLocations(); }}
        isEmailVerified={isEmailVerified}
      />
      <GlobalCapacityBulkUpload
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onUploaded={(result) => {
          snackbarShowMessage(
            `Uploaded capacity — ${result.rowsAccepted} row(s) accepted`,
            'success',
          );
          void fetchLocations();
        }}
        onError={(msg) => snackbarShowMessage(msg, 'error')}
      />
    </SecurSpaceContainer>
  );
};

export default LocationsProfile;
